/* Master · Racks — storage-location master and the medicine → home-shelf map.
 *
 * Two tabs, because these are two jobs:
 *   Racks            — the shelves themselves (create / rename / deactivate).
 *   Medicine locations — which shelf each stocked medicine belongs on. This is
 *                        what prefills the rack on every GRN line, so the
 *                        "needs a location" worklist is the screen's real work.
 *
 * Backed by rackService (v1/racks). Mirrors the Doctors / Suppliers masters:
 * list + a right-hand slide-in drawer, supplyKit inline styles.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import supplyService from "../../../Services/supplyService";
import { getResolvedWarehouseCode, isPharmacyHead } from "../../../Services/warehouseContext";
import { useWarehouse } from "../../Layout/WarehouseContext";
import { cbFmtINR, cbFmtNum } from "../supplyData";
import {
  Button,
  Pill,
  Input,
  Select,
  Table,
  Tr,
  Td,
  TabBar,
  SectionHeader,
  ToolbarBar,
  Eyebrow,
  LoadingState,
  ErrorState,
  InlineAlert,
  EmptyState,
  IconPlus,
  IconX,
  IconCheck,
  IconChevRight,
  IconPackage,
} from "../supplyKit";
import { WarehouseSelect } from "../warehousePicker";
import rackService, { rackCodeError } from "./rackService";
import { RackPicker } from "./RackPicker";

const fieldInput = {
  width: "100%",
  height: 30,
  padding: "0 8px",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  background: "var(--bg-panel)",
  color: "var(--fg-default)",
  outline: "none",
};
const fieldLabel = {
  display: "block",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  marginBottom: 4,
};
const Field = ({ label, children }) => (
  <div>
    <label style={fieldLabel}>{label}</label>
    {children}
  </div>
);

const blankRack = (warehouseId) => ({
  warehouseId: warehouseId || getResolvedWarehouseCode() || "",
  code: "",
  name: "",
  description: "",
  isActive: true,
});

const Racks = () => {
  /* Store and store list both come from the app-wide selection (topbar). */
  const { code: wh, isAll, warehouses } = useWarehouse();
  const [tab, setTab] = useState("racks");

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Racks"
        sub="Storage-location master · racks, and the shelf each medicine lives on"
      />
      <TabBar
        tabs={[
          { id: "racks", label: "Racks" },
          { id: "locations", label: "Medicine locations" },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === "racks" ? (
        <RackMaster wh={wh} isAll={isAll} warehouses={warehouses} />
      ) : (
        <MedicineLocations wh={wh} isAll={isAll} warehouses={warehouses} />
      )}
    </div>
  );
};

/* ---------------- Tab 1 · the rack master ---------------- */
const RackMaster = ({ wh, isAll, warehouses }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [racks, setRacks] = useState([]);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState({ page: 0, size: 20, totalPages: 0, totalElements: 0 });
  const [drawer, setDrawer] = useState(null); // { editing } | null

  useEffect(() => {
    const t = setTimeout(() => setSearch(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);

  /* Any filter change puts the pager back on page 1 — page 3 of the old result
     set is meaningless against the new one. */
  useEffect(() => {
    setPage((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [search, activeOnly, wh]);

  useEffect(() => {
    if (!wh) {
      setRacks([]);
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    rackService
      .list({ warehouseId: wh, activeOnly, search, page: page.page, size: page.size })
      .then((res) => {
        if (!alive) return;
        setRacks(res.content || []);
        setPage((p) => ({ ...p, totalPages: res.totalPages || 0, totalElements: res.totalElements || 0 }));
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.message || e?.message || "Could not load racks.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, activeOnly, search, page.page, page.size, reloadKey]);

  const start = page.totalElements === 0 ? 0 : page.page * page.size + 1;
  const end = Math.min(page.totalElements, (page.page + 1) * page.size);

  return (
    <>
      <ToolbarBar>
        <Input
          placeholder="Search code / name / description…"
          style={{ width: 280, maxWidth: "100%" }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select value={activeOnly ? "active" : "all"} onChange={(e) => setActiveOnly(e.target.value === "active")}>
          <option value="active">Active only</option>
          <option value="all">Include deactivated</option>
        </Select>
        <div style={{ marginLeft: "auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
            {cbFmtNum(page.totalElements)} rack{page.totalElements !== 1 ? "s" : ""}
          </span>
          <Button
            variant="primary"
            icon={<IconPlus size={14} />}
            disabled={!wh}
            onClick={() => setDrawer({ editing: null })}
          >
            New rack
          </Button>
        </div>
      </ToolbarBar>

      {!wh ? (
        <EmptyState
          icon={<IconPackage size={18} />}
          title={isAll ? "Pick a single store" : "Pick a store"}
          hint="A rack is a shelf in one store, so this screen works one store at a time."
        />
      ) : loading ? (
        <LoadingState label="Loading racks…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          <Table
            columns={[
              { label: "Code" },
              { label: "Name" },
              { label: "Description" },
              { label: "Medicines", align: "right" },
              { label: "Lots", align: "right" },
              { label: "Status" },
              { label: "", width: 24 },
            ]}
          >
            {racks.map((r) => (
              <Tr key={r.id} onClick={() => setDrawer({ editing: r })}>
                <Td mono style={{ fontWeight: 600, color: "var(--fg-strong)" }}>
                  {r.code}
                </Td>
                <Td>{r.name || "—"}</Td>
                <Td style={{ color: "var(--fg-muted)" }}>{r.description || "—"}</Td>
                <Td mono right>
                  {cbFmtNum(r.medicineCount)}
                </Td>
                <Td mono right>
                  {cbFmtNum(r.lotCount)}
                </Td>
                <Td>
                  <Pill variant={r.isActive ? "green" : "neutral"} dot>
                    {r.isActive ? "active" : "deactivated"}
                  </Pill>
                </Td>
                <Td>
                  <IconChevRight size={12} style={{ color: "var(--fg-muted)" }} />
                </Td>
              </Tr>
            ))}
            {racks.length === 0 && (
              <Tr>
                <Td colSpan={7} style={{ textAlign: "center", color: "var(--fg-muted)", padding: 20 }}>
                  No racks{search ? " match your search" : " in this store yet"}.
                </Td>
              </Tr>
            )}
          </Table>

          <ToolbarBar>
            <Select
              value={page.size}
              onChange={(e) => setPage((p) => ({ ...p, size: Number(e.target.value), page: 0 }))}
            >
              {[20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Select>
            <div style={{ marginLeft: "auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {start}–{end} of {cbFmtNum(page.totalElements)}
              </span>
              <Button
                size="sm"
                disabled={page.page <= 0}
                onClick={() => setPage((p) => ({ ...p, page: Math.max(0, p.page - 1) }))}
              >
                ← Prev
              </Button>
              <Button
                size="sm"
                disabled={page.page + 1 >= page.totalPages}
                onClick={() => setPage((p) => ({ ...p, page: p.page + 1 }))}
              >
                Next →
              </Button>
            </div>
          </ToolbarBar>
        </>
      )}

      {drawer && (
        <RackDrawer
          editing={drawer.editing}
          warehouseId={wh}
          warehouses={warehouses}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            setDrawer(null);
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </>
  );
};

/* ---------------- Add / edit rack drawer ---------------- */
export const RackDrawer = ({ editing, warehouseId, warehouses, onClose, onSaved }) => {
  const isEditing = Boolean(editing?.id);
  const [form, setForm] = useState(() =>
    isEditing ? { ...blankRack(warehouseId), ...editing } : blankRack(warehouseId)
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  /* What is in the rack — loaded only when editing, because only then is there
     anything to show. `null` = not loaded yet. */
  const [contents, setContents] = useState(null);
  const [confirmOff, setConfirmOff] = useState(false);

  const setF = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  useEffect(() => {
    if (!isEditing) return undefined;
    let alive = true;
    Promise.all([
      rackService.medicinesIn(editing.id).catch(() => []),
      rackService.lotsIn(editing.id).catch(() => []),
    ]).then(([medicines, lots]) => {
      if (alive) setContents({ medicines, lots });
    });
    return () => {
      alive = false;
    };
  }, [isEditing, editing?.id]);

  const codeErr = form.code ? rackCodeError(form.code) : null;
  const valid = Boolean(form.warehouseId) && Boolean(String(form.code).trim()) && !codeErr;

  const handleSave = () => {
    if (!valid) return;
    setSaving(true);
    setErr(null);
    const call = isEditing ? rackService.update(editing.id, form) : rackService.create(form);
    call
      .then((saved) => {
        toast.success(`Rack ${isEditing ? "updated" : "created"} — ${saved.code || form.code}.`);
        onSaved?.(saved);
      })
      .catch((e) => {
        setSaving(false);
        setErr(e?.response?.data?.message || e?.message || "Could not save the rack.");
      });
  };

  const handleDeactivate = () => {
    setSaving(true);
    setErr(null);
    rackService
      .deactivate(editing.id)
      .then(() => {
        toast.success(`Rack ${editing.code} deactivated.`);
        onSaved?.();
      })
      .catch((e) => {
        setSaving(false);
        setConfirmOff(false);
        /* 422 while the rack holds stock or is a medicine's home — the backend
           message names the reason, so show it verbatim. */
        setErr(e?.response?.data?.message || e?.message || "Could not deactivate the rack.");
      });
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 70 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          background: "var(--bg-canvas)",
          boxShadow: "-12px 0 40px rgba(13,17,27,0.18)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        className="cb-animate-in w-full sm:w-[min(520px,96vw)]"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 20px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              background: "var(--ink-50)",
              color: "var(--ink-700)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconPackage size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fg-strong)" }}>
              {isEditing ? `Rack ${editing.code}` : "New rack"}
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>Storage location</div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 0,
              background: "transparent",
              cursor: "pointer",
              color: "var(--fg-muted)",
              width: 28,
              height: 28,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconX size={16} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "16px 20px" }} className="cb-scroll">
          <Eyebrow style={{ display: "block", marginBottom: 10 }}>Rack details</Eyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Store *">
                {/* A rack belongs to exactly one store, and moving it between
                    stores is not a thing — so it is fixed once created. */}
                <WarehouseSelect
                  value={form.warehouseId || ""}
                  onChange={(v) => setF("warehouseId", v)}
                  warehouses={warehouses}
                  noneLabel="— Select store —"
                  disabled={isEditing || !isPharmacyHead()}
                  width="100%"
                />
              </Field>
            </div>
            <Field label="Code *">
              <input
                value={form.code}
                onChange={(e) => setF("code", e.target.value)}
                placeholder="R-05"
                maxLength={20}
                style={{
                  ...fieldInput,
                  fontFamily: "var(--font-mono)",
                  textTransform: "uppercase",
                  border: codeErr ? "1px solid var(--red-500)" : fieldInput.border,
                }}
              />
              {codeErr ? (
                <div style={{ fontSize: 10, color: "var(--red-700)", marginTop: 4 }}>{codeErr}</div>
              ) : (
                <div style={{ fontSize: 10, color: "var(--fg-muted)", marginTop: 4 }}>
                  Unique in the store · stored upper-cased
                </div>
              )}
            </Field>
            <Field label="Name">
              <input
                value={form.name}
                onChange={(e) => setF("name", e.target.value)}
                placeholder="Antibiotics"
                style={fieldInput}
              />
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Description">
                <input
                  value={form.description}
                  onChange={(e) => setF("description", e.target.value)}
                  placeholder="Second aisle, right wall · room temperature"
                  style={fieldInput}
                />
              </Field>
            </div>
            {isEditing && (
              <Field label="Status">
                <Pill variant={form.isActive ? "green" : "neutral"} dot>
                  {form.isActive ? "active" : "deactivated"}
                </Pill>
              </Field>
            )}
          </div>

          {err && (
            <div style={{ marginTop: 12 }}>
              <InlineAlert>{err}</InlineAlert>
            </div>
          )}

          {isEditing && (
            <div style={{ marginTop: 20 }}>
              <Eyebrow style={{ display: "block", marginBottom: 8 }}>
                What is here {contents ? `· ${contents.medicines.length} home medicines · ${contents.lots.length} lots` : ""}
              </Eyebrow>
              {!contents ? (
                <LoadingState label="Loading rack contents…" />
              ) : (
                <>
                  <div style={{ fontSize: 11, color: "var(--fg-muted)", marginBottom: 6 }}>
                    Medicines whose home this is
                  </div>
                  <Table columns={[{ label: "Medicine" }, { label: "Code" }, { label: "Shelf" }]}>
                    {contents.medicines.map((m) => (
                      <Tr key={m.medicineCode}>
                        <Td>{m.medicineName || "—"}</Td>
                        <Td mono>{m.medicineCode}</Td>
                        <Td style={{ color: "var(--fg-muted)" }}>{m.shelfPosition || "—"}</Td>
                      </Tr>
                    ))}
                    {contents.medicines.length === 0 && (
                      <Tr>
                        <Td colSpan={3} style={{ textAlign: "center", color: "var(--fg-muted)", padding: 14 }}>
                          No medicine calls this rack home.
                        </Td>
                      </Tr>
                    )}
                  </Table>

                  <div style={{ fontSize: 11, color: "var(--fg-muted)", margin: "14px 0 6px" }}>
                    Stock physically here
                  </div>
                  <Table
                    columns={[
                      { label: "Medicine" },
                      { label: "Batch" },
                      { label: "Expiry" },
                      { label: "Qty", align: "right" },
                      { label: "Value at MRP", align: "right" },
                    ]}
                  >
                    {contents.lots.map((l) => (
                      <Tr key={l.id}>
                        <Td>{l.medicineName || l.medId}</Td>
                        <Td mono>{l.batch}</Td>
                        <Td mono>{l.exp}</Td>
                        <Td mono right>
                          {cbFmtNum(l.qtyA)}
                        </Td>
                        <Td mono right>
                          {cbFmtINR(l.qtyA * l.mrp)}
                        </Td>
                      </Tr>
                    ))}
                    {contents.lots.length === 0 && (
                      <Tr>
                        <Td colSpan={5} style={{ textAlign: "center", color: "var(--fg-muted)", padding: 14 }}>
                          Empty — no stock in this rack.
                        </Td>
                      </Tr>
                    )}
                  </Table>
                </>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            padding: "12px 20px",
            borderTop: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <Button onClick={onClose}>Cancel</Button>
          {isEditing && form.isActive && (
            <Button variant="danger" disabled={saving} onClick={() => setConfirmOff(true)}>
              Deactivate
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <span
            style={{
              fontSize: 11,
              color: valid ? "var(--green-700)" : "var(--fg-muted)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {valid ? "Ready to save" : !form.warehouseId ? "Store required" : "Rack code required"}
          </span>
          <Button variant="primary" icon={<IconCheck size={14} />} disabled={!valid || saving} onClick={handleSave}>
            {saving ? "Saving…" : isEditing ? "Save changes" : "Create rack"}
          </Button>
        </div>
      </div>

      {confirmOff && (
        <ConfirmDialog
          title={`Deactivate rack ${editing.code}?`}
          message="It stops being offered on new receipts. A rack that still holds stock, or is some medicine's home shelf, cannot be deactivated — clear it first."
          confirmLabel="Deactivate"
          onCancel={() => setConfirmOff(false)}
          onConfirm={handleDeactivate}
        />
      )}
    </div>
  );
};

/* ---------------- Tab 2 · medicine → home shelf ---------------- */
const MedicineLocations = ({ wh, isAll, warehouses }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [racks, setRacks] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [q, setQ] = useState("");
  /* Staged rack-out for the worklist, keyed by medicine code:
     { rackId, shelfPosition }. Held locally until Assign, because racking out a
     store is one pass down the aisles, not one save per medicine. */
  const [staged, setStaged] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  /* Medicine code currently being written by a single-row edit — disables that
     row's controls so a double-click can't fire two upserts. */
  const [busyCode, setBusyCode] = useState(null);

  useEffect(() => {
    if (!wh) {
      setRacks([]);
      setAssigned([]);
      setUnassigned([]);
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    setStaged({});
    Promise.all([
      rackService.listAll({ warehouseId: wh }),
      rackService.assignments({ warehouseId: wh }),
      rackService.unassigned({ warehouseId: wh }),
    ])
      .then(([rackList, assignments, worklist]) => {
        if (!alive) return;
        setRacks(rackList || []);
        setAssigned(assignments || []);
        setUnassigned(worklist || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.message || e?.message || "Could not load medicine locations.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, reloadKey]);

  const needle = q.trim().toLowerCase();
  const match = (...parts) => !needle || parts.filter(Boolean).join(" ").toLowerCase().includes(needle);
  const worklistRows = useMemo(
    () => unassigned.filter((m) => match(m.medicineCode, m.medicineName)),
    [unassigned, needle] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const assignedRows = useMemo(
    () => assigned.filter((a) => match(a.medicineCode, a.medicineName, a.rackCode, a.rackName, a.shelfPosition)),
    [assigned, needle] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const stageFor = (code) => staged[code] || { rackId: null, shelfPosition: "" };
  const setStage = (code, patch) =>
    setStaged((s) => ({ ...s, [code]: { ...stageFor(code), ...patch } }));

  /* Only rows that actually got a rack are sent — a blank row is one the
     operator skipped, not an error to report. */
  const pending = Object.entries(staged)
    .filter(([, v]) => v.rackId)
    .map(([medicineCode, v]) => ({
      medicineCode,
      rackId: v.rackId,
      shelfPosition: v.shelfPosition,
      medicineName: unassigned.find((m) => m.medicineCode === medicineCode)?.medicineName,
    }));

  const saveStaged = () => {
    if (!pending.length) return;
    setSaving(true);
    setSaveErr(null);
    rackService
      .assignBulk(pending)
      .then((saved) => {
        toast.success(`${saved.length} medicine${saved.length !== 1 ? "s" : ""} racked out.`);
        setSaving(false);
        setReloadKey((k) => k + 1);
      })
      .catch((e) => {
        setSaving(false);
        /* All-or-nothing: nothing was written, so the staged edits stay put. */
        setSaveErr(e?.response?.data?.message || e?.message || "Could not save the assignments.");
      });
  };

  /* Move an already-mapped medicine, or change its shelf note. Upsert, so the
     same call covers both. */
  const applyAssignment = (row, patch) => {
    const rackId = patch.rackId !== undefined ? patch.rackId : row.rackId;
    if (!rackId) return;
    setBusyCode(row.medicineCode);
    rackService
      .assign({
        medicineCode: row.medicineCode,
        rackId,
        medicineName: row.medicineName,
        shelfPosition: patch.shelfPosition !== undefined ? patch.shelfPosition : row.shelfPosition,
      })
      .then((saved) => {
        setBusyCode(null);
        setAssigned((list) => list.map((a) => (a.medicineCode === saved.medicineCode ? saved : a)));
      })
      .catch((e) => {
        setBusyCode(null);
        toast.error(e?.response?.data?.message || e?.message || "Could not move the medicine.");
      });
  };

  const removeAssignment = (row) => {
    setBusyCode(row.medicineCode);
    rackService
      .unassign({ warehouseId: wh, medicineCode: row.medicineCode })
      .then(() => {
        setBusyCode(null);
        toast.success(`${row.medicineName || row.medicineCode} unassigned.`);
        setReloadKey((k) => k + 1);
      })
      .catch((e) => {
        setBusyCode(null);
        toast.error(e?.response?.data?.message || e?.message || "Could not unassign the medicine.");
      });
  };

  return (
    <>
      <ToolbarBar>
        <Input
          placeholder="Search medicine / code / rack…"
          style={{ width: 280, maxWidth: "100%" }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(assigned.length)} mapped · {cbFmtNum(unassigned.length)} to do
        </div>
      </ToolbarBar>

      {!wh ? (
        <EmptyState
          icon={<IconPackage size={18} />}
          title={isAll ? "Pick a single store" : "Pick a store"}
          hint="A medicine's shelf is specific to one store, so this screen works one store at a time."
        />
      ) : loading ? (
        <LoadingState label="Loading medicine locations…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          {racks.length === 0 && (
            <InlineAlert tone="amber">
              No active racks in this store yet — create them on the Racks tab before mapping medicines.
            </InlineAlert>
          )}

          <div>
            <Eyebrow style={{ display: "block", marginBottom: 6 }}>
              Needs a location · {cbFmtNum(unassigned.length)} stocked medicine
              {unassigned.length !== 1 ? "s" : ""} with no shelf
            </Eyebrow>
            <Table
              columns={[
                { label: "Medicine" },
                { label: "Code" },
                { label: "Rack" },
                { label: "Shelf position" },
              ]}
            >
              {worklistRows.map((m) => {
                const stage = stageFor(m.medicineCode);
                return (
                  <Tr key={m.medicineCode}>
                    <Td style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{m.medicineName || "—"}</Td>
                    <Td mono>{m.medicineCode}</Td>
                    <Td>
                      <RackPicker
                        value={stage.rackId}
                        racks={racks}
                        width={140}
                        height={26}
                        placeholder="— pick rack —"
                        onChange={(rackId) => setStage(m.medicineCode, { rackId })}
                      />
                    </Td>
                    <Td>
                      <input
                        value={stage.shelfPosition}
                        onChange={(e) => setStage(m.medicineCode, { shelfPosition: e.target.value })}
                        placeholder="shelf 2, left"
                        maxLength={100}
                        style={{ ...fieldInput, height: 26, width: 200 }}
                      />
                    </Td>
                  </Tr>
                );
              })}
              {worklistRows.length === 0 && (
                <Tr>
                  <Td colSpan={4} style={{ textAlign: "center", color: "var(--fg-muted)", padding: 20 }}>
                    {unassigned.length === 0
                      ? "Every stocked medicine has a location. "
                      : "No medicines match your search."}
                  </Td>
                </Tr>
              )}
            </Table>
            {saveErr && (
              <div style={{ marginTop: 8 }}>
                <InlineAlert>{saveErr}</InlineAlert>
              </div>
            )}
            {pending.length > 0 && (
              <ToolbarBar>
                <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>
                  {pending.length} medicine{pending.length !== 1 ? "s" : ""} ready · one bad entry rejects the batch
                </span>
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <Button size="sm" onClick={() => setStaged({})} disabled={saving}>
                    Discard
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<IconCheck size={13} />}
                    onClick={saveStaged}
                    disabled={saving}
                  >
                    {saving ? "Assigning…" : `Assign ${pending.length}`}
                  </Button>
                </div>
              </ToolbarBar>
            )}
          </div>

          <div style={{ marginTop: 8 }}>
            <Eyebrow style={{ display: "block", marginBottom: 6 }}>
              Mapped · {cbFmtNum(assigned.length)} medicine{assigned.length !== 1 ? "s" : ""}
            </Eyebrow>
            <Table
              columns={[
                { label: "Medicine" },
                { label: "Code" },
                { label: "Rack" },
                { label: "Shelf position" },
                { label: "Reads as" },
                { label: "", width: 24 },
              ]}
            >
              {assignedRows.map((a) => (
                <AssignedRow
                  key={a.medicineCode}
                  row={a}
                  racks={racks}
                  busy={busyCode === a.medicineCode}
                  onMove={(rackId) => applyAssignment(a, { rackId })}
                  onShelf={(shelfPosition) => applyAssignment(a, { shelfPosition })}
                  onRemove={() => removeAssignment(a)}
                />
              ))}
              {assignedRows.length === 0 && (
                <Tr>
                  <Td colSpan={6} style={{ textAlign: "center", color: "var(--fg-muted)", padding: 20 }}>
                    {assigned.length === 0 ? "Nothing mapped yet." : "No medicines match your search."}
                  </Td>
                </Tr>
              )}
            </Table>
          </div>
        </>
      )}
    </>
  );
};

/* One mapped medicine. The shelf note saves on blur (and Enter) rather than per
   keystroke — a PUT per character would be one upsert per letter typed. */
const AssignedRow = ({ row, racks, busy, onMove, onShelf, onRemove }) => {
  const [shelf, setShelf] = useState(row.shelfPosition || "");

  useEffect(() => {
    setShelf(row.shelfPosition || "");
  }, [row.shelfPosition]);

  const commitShelf = () => {
    if (shelf === (row.shelfPosition || "")) return;
    onShelf(shelf);
  };

  return (
    <Tr>
      <Td style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{row.medicineName || "—"}</Td>
      <Td mono>{row.medicineCode}</Td>
      <Td>
        <RackPicker
          value={row.rackId}
          racks={racks}
          width={140}
          height={26}
          disabled={busy}
          fallbackLabel={row.rackCode}
          onChange={(rackId) => rackId && onMove(rackId)}
        />
        {row.rackActive === false && (
          <Pill variant="amber" dot>
            rack off
          </Pill>
        )}
      </Td>
      <Td>
        <input
          value={shelf}
          disabled={busy}
          onChange={(e) => setShelf(e.target.value)}
          onBlur={commitShelf}
          onKeyDown={(e) => e.key === "Enter" && commitShelf()}
          placeholder="shelf 2, left"
          maxLength={100}
          style={{ ...fieldInput, height: 26, width: 200 }}
        />
      </Td>
      <Td mono style={{ color: "var(--fg-muted)" }}>
        {row.locationLabel || row.rackCode}
      </Td>
      <Td>
        <button
          onClick={onRemove}
          disabled={busy}
          title="Remove this medicine's location"
          style={{
            border: 0,
            background: "transparent",
            cursor: busy ? "wait" : "pointer",
            color: "var(--red-700)",
            width: 22,
            height: 22,
            borderRadius: 4,
          }}
        >
          <IconX size={13} />
        </button>
      </Td>
    </Tr>
  );
};

/* Small confirm, matching the one in Grn.jsx. Local so the racks screen doesn't
   pull the GRN module in for a dialog. */
const ConfirmDialog = ({ title, message, confirmLabel = "Confirm", onConfirm, onCancel }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 90,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <div onClick={onCancel} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
    <div
      className="cb-animate-in"
      style={{
        position: "relative",
        width: "min(420px, 92vw)",
        background: "var(--bg-canvas)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
        boxShadow: "0 18px 48px rgba(13,17,27,0.24)",
        padding: 20,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--fg-strong)" }}>{title}</div>
      <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 8, lineHeight: 1.5 }}>{message}</div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="danger" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  </div>
);

export default Racks;
